import { useState, useRef, useEffect } from 'react';
import { useThemeEngine } from '../themes/ThemeProvider';
import { Icon } from './Icon';

interface ThemePickerProps {
  /** Show as compact icon button vs full dropdown */
  compact?: boolean;
}

/**
 * Theme picker component
 * Displays available themes with color previews
 */
export function ThemePicker({ compact = false }: ThemePickerProps) {
  const { theme, themeId, themePreviews, setThemeById } = useThemeEngine();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
    >
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          padding: compact ? 'var(--spacing-sm)' : 'var(--spacing-sm) var(--spacing-md)',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--color-text)',
          fontSize: 'var(--font-size-sm)',
          transition: 'var(--transition-fast)',
        }}
        title={`Theme: ${theme.name}`}
      >
        {/* Color swatch preview */}
        <div
          style={{
            display: 'flex',
            gap: '2px',
            padding: '2px',
            backgroundColor: theme.colors.background,
            borderRadius: '2px',
            border: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '1px',
              backgroundColor: theme.colors.primary,
            }}
          />
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '1px',
              backgroundColor: theme.colors.accent,
            }}
          />
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '1px',
              backgroundColor: theme.colors.surface,
            }}
          />
        </div>

        {!compact && (
          <>
            <span style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.02em' }}>
              {theme.name}
            </span>
            <Icon name="chevron-down" size={12} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 'var(--spacing-xs)',
            minWidth: '280px',
            backgroundColor: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              borderBottom: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <h4
              style={{
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-display)',
                color: 'var(--color-text)',
                margin: 0,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Select Theme
            </h4>
          </div>

          {/* Theme list */}
          <div
            style={{
              padding: 'var(--spacing-xs)',
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {themePreviews.map((preview) => {
              const isSelected = preview.id === themeId;

              return (
                <button
                  key={preview.id}
                  onClick={() => {
                    setThemeById(preview.id);
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-md)',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    backgroundColor: isSelected
                      ? 'rgba(var(--color-accent), 0.1)'
                      : 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'var(--transition-fast)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'var(--color-background-elevated)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* Color preview */}
                  <div
                    style={{
                      width: 48,
                      height: 36,
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${preview.previewColors.text}30`,
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Top bar - surface with accent */}
                    <div
                      style={{
                        height: '30%',
                        backgroundColor: preview.previewColors.surface,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 2,
                        gap: 1,
                      }}
                    >
                      <div
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: preview.previewColors.accent,
                        }}
                      />
                      <div
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: preview.previewColors.primary,
                        }}
                      />
                    </div>
                    {/* Main area - background */}
                    <div
                      style={{
                        flex: 1,
                        backgroundColor: preview.previewColors.background,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        padding: 2,
                      }}
                    >
                      {/* Mini tokens */}
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: preview.previewColors.primary,
                        }}
                      />
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: preview.previewColors.accent,
                        }}
                      />
                    </div>
                  </div>

                  {/* Theme info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text)',
                          fontWeight: isSelected ? 'var(--font-weight-bold)' : 'var(--font-weight-normal)',
                        }}
                      >
                        {preview.name}
                      </span>
                      {isSelected && (
                        <Icon name="check" size={12} color="var(--color-accent)" />
                      )}
                      {preview.isLight && (
                        <span
                          style={{
                            fontSize: '0.65rem',
                            padding: '1px 4px',
                            backgroundColor: 'var(--color-warning)',
                            color: 'var(--color-text-inverse)',
                            borderRadius: '2px',
                            textTransform: 'uppercase',
                            fontWeight: 'bold',
                          }}
                        >
                          Light
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        margin: 0,
                        marginTop: 2,
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {preview.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Inline theme swatch for use in settings
 */
export function ThemeSwatch({
  themeId,
  onClick,
  selected = false,
  size = 'md',
}: {
  themeId: string;
  onClick?: () => void;
  selected?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { getThemeById } = useThemeEngine();
  const theme = getThemeById(themeId);

  if (!theme) return null;

  const sizes = {
    sm: { width: 32, height: 24, gap: 2, dotSize: 4 },
    md: { width: 48, height: 36, gap: 3, dotSize: 6 },
    lg: { width: 64, height: 48, gap: 4, dotSize: 8 },
  };

  const s = sizes[size];

  return (
    <button
      onClick={onClick}
      style={{
        width: s.width,
        height: s.height,
        borderRadius: 'var(--radius-sm)',
        border: selected
          ? '2px solid var(--color-accent)'
          : '1px solid var(--color-border)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        padding: 0,
        backgroundColor: theme.colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: s.gap,
        transition: 'var(--transition-fast)',
      }}
      title={theme.name}
    >
      <div
        style={{
          width: s.dotSize,
          height: s.dotSize,
          borderRadius: '50%',
          backgroundColor: theme.colors.primary,
        }}
      />
      <div
        style={{
          width: s.dotSize,
          height: s.dotSize,
          borderRadius: '50%',
          backgroundColor: theme.colors.accent,
        }}
      />
      <div
        style={{
          width: s.dotSize,
          height: s.dotSize,
          borderRadius: '50%',
          backgroundColor: theme.colors.text,
        }}
      />
    </button>
  );
}
